import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import * as yup from 'yup';
import {
  ICaptableDataModel,
  IStakeholderInvestmentDataModel,
  StakeholderTBDName,
} from '../../../../data-models/captable2.data-model';
import { integerField, percentField, ViewModelInvestmentType } from '../../../../schemas/common-schema-defs';
import { fundsByIdMapAtom } from '../../../../services/state/AppConfigStateJ';
import { StringFormatterId } from '../../../../util/formatters/StringFormatters';
import { getShareClassType } from '../../../../view-models/captable.view-model';
import { isClientInvestment } from '../captable-utils';
import { CaptableGroupingMode } from './CaptableSummaryColDefs2';

/**
 * Schema fields for the Captable Summary Grid 2 row data.
 * Each row represents a single stakeholder investment with denormalized parent investment data
 * and normalized metric fields.
 */
export function captableSummaryRowData2Fields() {
  return {
    // Identity fields (from parent and stakeholder)
    stakeholderName: yup.string().required().label('Stakeholder').default(''),
    fundId: yup.number().nullable().optional().default(undefined),
    fundName: yup.string().nullable().optional().label('Fund').default(undefined),
    investmentName: yup.string().required().label('Investment').default(''),
    investmentType: yup
      .string()
      .oneOf(Object.values(ViewModelInvestmentType))
      .required()
      .label('Type')
      .gridMeta({
        formatter: StringFormatterId.capitalizedString,
        filter: 'agSetColumnFilter',
      })
      .default(ViewModelInvestmentType.common),
    investmentDate: yup.date().nullable().optional().default(null),

    // Normalized metric fields
    amount: yup
      .number()
      .nullable()
      .optional()
      .label('Invested Amount')
      .gridMeta({ formatter: 'currency', aggFunc: 'sum' })
      .default(null),
    sharesHeld: integerField()
      .nullable()
      .optional()
      .label('Fully Diluted Shares')
      .gridMeta({ aggFunc: 'sum' })
      .default(null),
    ownershipPercentage: percentField()
      .nullable()
      .optional()
      .label('Fully Diluted Ownership %')
      .gridMeta({ aggFunc: 'captableOwnershipAggFunc' })
      .default(null),
    outstandingShares: integerField()
      .nullable()
      .optional()
      .label('Outstanding Shares')
      .gridMeta({ aggFunc: 'sum' })
      .default(null),
    outstandingOwnershipPercentage: percentField()
      .nullable()
      .optional()
      .label('Outstanding Ownership %')
      .gridMeta({ aggFunc: 'captableOutstandingOwnershipAggFunc' })
      .default(null),
  };
}

export function captableSummaryRowData2GridSchema() {
  return yup.object().shape(captableSummaryRowData2Fields());
}

/**
 * Explicit interface for row data type safety.
 * Kept separate from schema inference to handle runtime type differences
 * (e.g., investmentDate can be string | Date | null from API).
 */
export interface ICaptableSummaryRowData2 {
  // From parent captableInvestment (denormalized)
  investmentName: string;
  investmentDate: string | Date | null;
  investmentType: ViewModelInvestmentType;

  // From stakeholderInvestment (identity fields)
  stakeholderName: string;
  fundId?: number;
  fundName?: string;

  // Normalized metric fields (mapped based on investmentType)
  amount?: number | null;
  sharesHeld?: number | null;
  ownershipPercentage?: number | null;
  outstandingShares?: number | null;
  outstandingOwnershipPercentage?: number | null;
}

type NormalizedMetrics = Pick<
  ICaptableSummaryRowData2,
  'amount' | 'sharesHeld' | 'ownershipPercentage' | 'outstandingShares'
>;

export const CaptableSummaryRowData2FieldsToShow = [
  'stakeholderName',
  'fundName',
  'investmentName',
  'investmentType',
  'amount',
  'sharesHeld',
  'ownershipPercentage',
  'outstandingShares',
  'outstandingOwnershipPercentage',
  'investmentDate',
] as const;

/**
 * Maps source fields from stakeholder investment to normalized metric fields
 * based on investment type.
 *
 * Mapping logic mirrors getInvestmentColumns() from CaptableSummaryColDefs.ts:
 * - amount: cashRaised (preferred/common/warrants/cash/debt) or principal-then-cashRaised (notes)
 * - sharesHeld: fullyDilutedShares (preferred/common/warrants) or options derivatives sum (options)
 * - ownershipPercentage: ownershipPercentage (preferred/common/warrants/options)
 * - outstandingShares: outstandingShares (preferred/common) or outstandingWarrants (warrants)
 */
export function getNormalizedMetrics(
  stakeholderInv: IStakeholderInvestmentDataModel,
  investmentType: ViewModelInvestmentType
): NormalizedMetrics {
  switch (investmentType) {
    case ViewModelInvestmentType.preferred:
    case ViewModelInvestmentType.common:
      return {
        amount: stakeholderInv.cashRaised,
        sharesHeld: stakeholderInv.fullyDilutedShares,
        ownershipPercentage: stakeholderInv.ownershipPercentage,
        outstandingShares: stakeholderInv.outstandingShares,
      };
    case ViewModelInvestmentType.warrants:
      return {
        amount: stakeholderInv.cashRaised,
        sharesHeld: stakeholderInv.fullyDilutedShares,
        ownershipPercentage: stakeholderInv.ownershipPercentage,
        outstandingShares: stakeholderInv.outstandingWarrants, // normalized from outstandingWarrants
      };
    case ViewModelInvestmentType.notes:
      return {
        // principal is the note's invested amount; Carta aggregates can leave it 0/unset, so fall back to cashRaised.
        amount: stakeholderInv.principal || stakeholderInv.cashRaised,
        sharesHeld: null,
        ownershipPercentage: null,
        outstandingShares: null,
      };
    case ViewModelInvestmentType.options: {
      return {
        amount: null,
        sharesHeld:
          (stakeholderInv.outstandingEquityAwardDerivatives ?? 0) +
          (stakeholderInv.outstandingCommittedRestrictedStockAwards ?? 0),
        ownershipPercentage: stakeholderInv.ownershipPercentage,
        outstandingShares: null,
      };
    }
    case ViewModelInvestmentType.cash:
    case ViewModelInvestmentType.debt:
      return {
        amount: stakeholderInv.cashRaised,
        sharesHeld: null,
        ownershipPercentage: null,
        outstandingShares: null,
      };
  }
}

/**
 * Calculates the outstanding ownership percentage for a stakeholder within an investment.
 * Returns null if the investment has no outstanding shares or the stakeholder has none.
 */
function calculateOutstandingOwnershipPercentage(
  outstandingShares: number | null | undefined,
  investmentTotal: number
): number | null {
  if (!investmentTotal || outstandingShares == null) {
    return null;
  }
  return (outstandingShares / investmentTotal) * 100;
}

// Investment type sort order (matches typical display order)
const InvestmentTypeSortOrder: Record<ViewModelInvestmentType, number> = {
  [ViewModelInvestmentType.preferred]: 0,
  [ViewModelInvestmentType.common]: 1,
  [ViewModelInvestmentType.options]: 2,
  [ViewModelInvestmentType.warrants]: 3,
  [ViewModelInvestmentType.notes]: 4,
  [ViewModelInvestmentType.cash]: 5,
  [ViewModelInvestmentType.debt]: 6,
};

function stakeholderComparator(a: ICaptableSummaryRowData2, b: ICaptableSummaryRowData2): number {
  // TBD always last
  if (a.stakeholderName === StakeholderTBDName) {
    return 1;
  }
  if (b.stakeholderName === StakeholderTBDName) {
    return -1;
  }

  // Client investments (with fundId) come first
  if (isClientInvestment(a) && !isClientInvestment(b)) {
    return -1;
  }
  if (!isClientInvestment(a) && isClientInvestment(b)) {
    return 1;
  }

  // Sort by ownership % descending
  return (b.ownershipPercentage ?? 0) - (a.ownershipPercentage ?? 0);
}

function shareClassComparator(a: ICaptableSummaryRowData2, b: ICaptableSummaryRowData2): number {
  // Primary: investment type order
  const typeOrderDiff = InvestmentTypeSortOrder[a.investmentType] - InvestmentTypeSortOrder[b.investmentType];
  if (typeOrderDiff !== 0) {
    return typeOrderDiff;
  }

  // Secondary: investment name alphabetically
  const nameDiff = a.investmentName.localeCompare(b.investmentName);
  if (nameDiff !== 0) {
    return nameDiff;
  }

  // Tertiary: stakeholder sorting
  return stakeholderComparator(a, b);
}

function sortRowData(
  rows: ICaptableSummaryRowData2[],
  groupingMode: CaptableGroupingMode
): ICaptableSummaryRowData2[] {
  const comparator =
    groupingMode === CaptableGroupingMode.shareClass ? shareClassComparator : stakeholderComparator;
  return [...rows].sort(comparator);
}

export interface ICaptableSummaryRowData2Result {
  rows: ICaptableSummaryRowData2[];
  totalSharesHeld: number;
  totalOutstandingShares: number;
}

export function useCaptableSummaryRowData2(
  captable: ICaptableDataModel | null,
  groupingMode: CaptableGroupingMode = CaptableGroupingMode.stakeholder
): ICaptableSummaryRowData2Result {
  const fundMap = useAtomValue(fundsByIdMapAtom);

  return useMemo(() => {
    if (!captable?.captableInvestments?.length) {
      return { rows: [], totalSharesHeld: 0, totalOutstandingShares: 0 };
    }

    // Phase 1: Build rows and accumulate investment totals in single pass
    const investmentOutstandingTotals = new Map<string, number>();
    const rowsWithoutPercentage: Omit<ICaptableSummaryRowData2, 'outstandingOwnershipPercentage'>[] = [];

    captable.captableInvestments.forEach((investment) => {
      const investmentType = getShareClassType(investment.investmentType, investment.shareClassType);
      const investmentName = investment.name;
      if (!investmentType || !investmentName) {
        return;
      }

      investment.stakeholderInvestments?.forEach((stakeholder) => {
        if (!stakeholder.stakeholderName && stakeholder.fundId == null) {
          return;
        }

        const resolvedFundName =
          fundMap.get(stakeholder.fundId ?? -1)?.name ?? stakeholder?.fundName ?? undefined;
        const normalizedMetrics = getNormalizedMetrics(stakeholder, investmentType);

        // Accumulate investment outstanding total
        const currentTotal = investmentOutstandingTotals.get(investmentName) ?? 0;
        investmentOutstandingTotals.set(
          investmentName,
          currentTotal + (normalizedMetrics.outstandingShares ?? 0)
        );

        rowsWithoutPercentage.push({
          investmentName,
          investmentDate: investment.investmentDate ?? null,
          investmentType,
          stakeholderName: stakeholder.stakeholderName ?? resolvedFundName ?? `Fund ${stakeholder.fundId}`,
          fundId: stakeholder.fundId ?? undefined,
          fundName: resolvedFundName,
          ...normalizedMetrics,
        });
      });
    });

    // Phase 2: Add outstandingOwnershipPercentage using accumulated totals
    const rows: ICaptableSummaryRowData2[] = rowsWithoutPercentage.map((row) => ({
      ...row,
      outstandingOwnershipPercentage: calculateOutstandingOwnershipPercentage(
        row.outstandingShares,
        investmentOutstandingTotals.get(row.investmentName) ?? 0
      ),
    }));

    // Phase 3: Calculate grand totals and sort
    const totalSharesHeld = rows.reduce((sum, row) => sum + (row.sharesHeld ?? 0), 0);
    const totalOutstandingShares = rows.reduce((sum, row) => sum + (row.outstandingShares ?? 0), 0);

    return {
      rows: sortRowData(rows, groupingMode),
      totalSharesHeld,
      totalOutstandingShares,
    };
  }, [captable, fundMap, groupingMode]);
}
